import { useQuery } from '@tanstack/react-query';
import {
  customerService,
  subscriptionService,
  requestService,
  taskService,
  walletService,
  dashboardService,
  locationService,
  planService,
  sessionsService,
  subscriberSessionsService,
} from '@/services';
import { apiClient } from '@/services/api-client';
import { useAuthStore } from '@/stores';

export interface CustomerQueryParams {
  pageSize?: number;
  pageNumber?: number;
  sortProperty?: string;
  sortDirection?: string;
  name?: string;
  phone?: string;
  customerId?: string;
  email?: string;
  ontSerial?: string;
  zoneId?: string;
  zone?: string;
  hierarchyLevel?: string | number;
  includeSubPartners?: boolean;
}

// ===== Customers =====
export const useCustomers = (params?: CustomerQueryParams) => {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: async () => {
      const hierarchy = params?.includeSubPartners !== undefined
        ? (params.includeSubPartners ? 1 : 0)
        : params?.hierarchyLevel;

      const response = await customerService.list({
        pageSize: params?.pageSize ?? 10,
        pageNumber: params?.pageNumber ?? 1,
        'sortCriteria.property': params?.sortProperty ?? 'self.displayValue',
        'sortCriteria.direction': params?.sortDirection ?? 'asc',
        ...(params?.name ? { name: params.name } : {}),
        ...(params?.phone ? { phone: params.phone } : {}),
        ...(params?.customerId ? { customerId: params.customerId } : {}),
        ...(params?.email ? { email: params.email } : {}),
        ...(params?.ontSerial ? { ontSerial: params.ontSerial } : {}),
        ...(params?.zoneId ? { zoneId: params.zoneId } : {}),
        ...(params?.zone ? { zone: params.zone } : {}),
        ...(hierarchy !== undefined ? { hierarchyLevel: hierarchy } : {}),
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
};

export const useCustomerSummary = () => {
  return useQuery({
    queryKey: ['customerSummary'],
    queryFn: async () => {
      const response = await customerService.summary();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCustomerDetails = (id: string | null) => {
  return useQuery({
    queryKey: ['customerDetails', id],
    queryFn: async () => {
      if (!id) return null;
      const [detailsRes, subsRes, tasksRes, addressesRes, scheduledChangesRes] = await Promise.all([
        customerService.get(id),
        customerService.subscriptions(id).catch(() => ({ data: null })),
        customerService.tasks(id).catch(() => ({ data: null })),
        customerService.addresses([id]).catch(() => ({ data: null })),
        customerService.scheduledChanges(id).catch(() => ({ data: null })),
      ]);

      const subs = subsRes.data?.items || subsRes.data?.data || (Array.isArray(subsRes.data) ? subsRes.data : []);
      const subIds = subs.map((s: any) => s.self?.id || s.id).filter(Boolean);

      let allowedActionsData = null;
      let paymentMethodsData = null;

      if (subIds.length > 0) {
        const [actionsRes, pmRes] = await Promise.all([
          subscriptionService.allowedActions(subIds, id).catch(() => ({ data: null })),
          walletService.paymentMethods(subIds).catch(() => ({ data: null })),
        ]);
        allowedActionsData = actionsRes.data;
        paymentMethodsData = pmRes.data;
      }

      return {
        details: detailsRes.data,
        subscriptions: subsRes.data,
        tasks: tasksRes.data,
        addresses: addressesRes.data,
        scheduledChanges: scheduledChangesRes?.data,
        allowedActions: allowedActionsData,
        paymentMethods: paymentMethodsData,
      };
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// ===== Subscriptions =====
export const useSubscriptions = (params?: {
  pageSize?: number;
  pageNumber?: number;
  sortProperty?: string;
  sortDirection?: string;
}) => {
  return useQuery({
    queryKey: ['subscriptions', params],
    queryFn: async () => {
      const response = await subscriptionService.list({
        pageSize: params?.pageSize ?? 10,
        pageNumber: params?.pageNumber ?? 1,
        'sortCriteria.property': params?.sortProperty ?? 'expires',
        'sortCriteria.direction': params?.sortDirection ?? 'asc',
        hierarchyLevel: 0,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
};

export const useSubscriptionStatuses = () => {
  return useQuery({
    queryKey: ['subscriptionStatuses'],
    queryFn: async () => {
      const response = await subscriptionService.statuses();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useAllowedActions = (subscriptionIds: (number | string)[], enabled = true) => {
  return useQuery({
    queryKey: ['allowedActions', subscriptionIds],
    queryFn: async () => {
      if (subscriptionIds.length === 0) return { totalCount: 0, items: [] };
      const response = await subscriptionService.allowedActions(subscriptionIds);
      return response.data;
    },
    enabled: enabled && subscriptionIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePaymentMethods = (subscriptionIds: (number | string)[], enabled = true) => {
  return useQuery({
    queryKey: ['paymentMethods', subscriptionIds],
    queryFn: async () => {
      if (subscriptionIds.length === 0) return { totalCount: 0, items: [] };
      const response = await walletService.paymentMethods(subscriptionIds);
      return response.data;
    },
    enabled: enabled && subscriptionIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCustomersSummaryByIds = (customerIds: (number | string)[], enabled = true) => {
  return useQuery({
    queryKey: ['customersSummaryByIds', customerIds],
    queryFn: async () => {
      if (customerIds.length === 0) return { totalCount: 0, items: [] };
      const response = await customerService.summaryByIds(customerIds);
      return response.data;
    },
    enabled: enabled && customerIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

// ===== Requests =====
export const useRequests = (params?: {
  pageSize?: number;
  pageNumber?: number;
  status?: string;
  typeIds?: string;
  additionalFilters?: string[];
}) => {
  return useQuery({
    queryKey: ['requests', params],
    queryFn: async () => {
      const response = await requestService.list({
        pageSize: params?.pageSize ?? 10,
        pageNumber: params?.pageNumber ?? 1,
        'sortCriteria.direction': 'desc',
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.typeIds ? { typeIds: params.typeIds } : {}),
        ...(params?.additionalFilters ? { additionalFilters: params.additionalFilters } : {}),
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
};

export const useRequestTypes = () => {
  return useQuery({
    queryKey: ['requestTypes'],
    queryFn: async () => {
      const response = await requestService.types();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useRequestStatuses = () => {
  return useQuery({
    queryKey: ['requestStatuses'],
    queryFn: async () => {
      const response = await requestService.statuses();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useRequestDetails = (requestId?: string) => {
  return useQuery({
    queryKey: ['requestDetails', requestId],
    enabled: !!requestId,
    queryFn: async () => {
      if (!requestId) return null;
      const [onboardingRes, commentsRes, logsRes, tasksRes] = await Promise.allSettled([
        requestService.customerOnboarding(requestId),
        requestService.comments(requestId),
        requestService.logs(requestId),
        taskService.list({ requestIds: [requestId], status: 0 }),
      ]);

      return {
        onboarding: onboardingRes.status === 'fulfilled' ? onboardingRes.value.data : null,
        comments: commentsRes.status === 'fulfilled' ? commentsRes.value.data : null,
        logs: logsRes.status === 'fulfilled' ? logsRes.value.data : null,
        tasks: tasksRes.status === 'fulfilled' ? tasksRes.value.data : null,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

// ===== Tasks =====
export const useTasks = (params?: { requestIds?: string[]; status?: number }) => {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: async () => {
      const response = await taskService.list(params);
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
};

export const useTasksSummary = () => {
  return useQuery({
    queryKey: ['tasksSummary'],
    queryFn: async () => {
      const response = await taskService.summary();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

// ===== Wallet =====
export const useWallet = (params?: { pageSize?: number; pageNumber?: number }) => {
  const storePartnerId = useAuthStore((s) => s.partnerId);

  return useQuery({
    queryKey: ['wallet', storePartnerId, params],
    enabled: true,
    queryFn: async () => {
      const effectivePartnerId = storePartnerId || '';

      // Use Promise.allSettled so balance is retrieved even if transactions takes longer or fails
      const [balanceResult, txResult] = await Promise.allSettled([
        walletService.balance(effectivePartnerId),
        walletService.transactions({
          partnerId: effectivePartnerId,
          pageSize: params?.pageSize ?? 10,
          pageNumber: params?.pageNumber ?? 1,
        }),
      ]);

      let balance = 0;
      if (balanceResult.status === 'fulfilled') {
        const balanceData = balanceResult.value?.data as any;
        balance = balanceData?.model?.balance ?? balanceData?.balance ?? balanceData?.currentBalance ?? 0;
      }

      let transactions: any[] = [];
      if (txResult.status === 'fulfilled') {
        const txData = txResult.value?.data as any;
        if (txData) {
          if (Array.isArray(txData)) {
            transactions = txData;
          } else if (Array.isArray(txData.items)) {
            transactions = txData.items;
          } else if (Array.isArray(txData.data)) {
            transactions = txData.data;
          }
        }
      }

      return {
        balance,
        transactions,
      };
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
};

// ===== Locations =====
export const useLocations = () => {
  return useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const response = await locationService.zones();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });
};

// ===== Dashboard =====
export const useDashboardStats = () => {
  const partnerId = useAuthStore((s) => s.partnerId);

  return useQuery({
    queryKey: ['dashboardStats', partnerId],
    queryFn: async () => {
      // Fetch all dashboard data in parallel
      const [partnerSummary, tasksSummary, ticketsSummary, requestsSummary] =
        await Promise.allSettled([
          dashboardService.summary(),
          dashboardService.tasksSummary(),
          dashboardService.supportTickets(),
          requestService.summary(),
        ]);

      return {
        partnerSummary:
          partnerSummary.status === 'fulfilled' ? partnerSummary.value.data : null,
        tasksSummary:
          tasksSummary.status === 'fulfilled' ? tasksSummary.value.data : null,
        ticketsSummary:
          ticketsSummary.status === 'fulfilled' ? ticketsSummary.value.data : null,
        requestsSummary:
          requestsSummary.status === 'fulfilled' ? requestsSummary.value.data : null,
      };
    },
    staleTime: 0,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    retry: 2,
  });
};

// ===== Plans/Bundles =====
export const usePlans = () => {
  return useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await planService.bundles(true);
      return response.data;
    },
    staleTime: 15 * 60 * 1000,
  });
};

// ===== Active Sessions =====
export const useActiveSessions = (location?: string) => {
  return useQuery({
    queryKey: ['activeSessions', location],
    queryFn: async () => {
      const response = await sessionsService.list({ location });
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

// ===== Subscriber Sessions (FTTH Portal Network Sessions) =====
export const useSubscriberSessions = (params?: {
  pageSize?: number;
  pageNumber?: number;
  status?: number;
  username?: string;
  hierarchyLevel?: number;
  sortCriteriaProperty?: string;
  sortCriteriaDirection?: string;
  zone?: string;
  datePreset?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const ftthToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['subscriberSessions', params, ftthToken],
    queryFn: async () => {
      const response = await subscriberSessionsService.list(params);
      return response.data;
    },
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useContractorAccountHealth = () => {
  const user = useAuthStore((s) => s.user);
  const username = user?.username || 'city';
  const isLocal = user?.authType === 'LOCAL';

  return useQuery({
    queryKey: ['contractorAccountHealth', username, isLocal],
    queryFn: async () => {
      if (isLocal) {
        return { hasAuthError: false, isHealthy: true, message: 'Local Edge Session Active' };
      }
      const response = await apiClient.get(`/contractor/account-health?username=${encodeURIComponent(username)}`);
      return response.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: 120 * 1000,
  });
};


